import { useState } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import { Calendar } from "~/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "~/ui/popover"
import { Button } from "~/ui/button"
import { cn } from "~/common/cn"
import { CalendarIcon, Clock } from "lucide-react"
import { format } from "date-fns"
import { Label } from "~/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/select"

interface DateTimePickerProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  required?: boolean
  hourOptions?: number[]
  minuteOptions?: number[]
}

export function DateTimePicker<T extends FieldValues>({
  control,
  name,
  label,
  required,
  hourOptions = Array.from({ length: 24 }, (_, i) => i),
  minuteOptions = [0, 15, 30, 45],
}: DateTimePickerProps<T>) {
  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={name} className="block">
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateTimePickerInput
            value={field.value}
            onChange={field.onChange}
            id={name}
            hourOptions={hourOptions}
            minuteOptions={minuteOptions}
          />
        )}
      />
    </div>
  )
}

interface DateTimePickerInputProps {
  value: Date
  onChange: (date: Date) => void
  id?: string
  hourOptions: number[]
  minuteOptions: number[]
}

function DateTimePickerInput({
  value,
  onChange,
  id,
  hourOptions,
  minuteOptions,
}: DateTimePickerInputProps) {
  const [date, setDate] = useState<Date | undefined>(value)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const handleDateChange = (newDate: Date | undefined) => {
    if (newDate) {
      const updatedDate = new Date(value)
      updatedDate.setFullYear(newDate.getFullYear())
      updatedDate.setMonth(newDate.getMonth())
      updatedDate.setDate(newDate.getDate())

      setDate(newDate)
      onChange(updatedDate)
      setIsCalendarOpen(false)
    }
  }

  const handleHourChange = (hour: string) => {
    const updatedDate = new Date(value)
    updatedDate.setHours(parseInt(hour))
    onChange(updatedDate)
  }

  const handleMinuteChange = (minute: string) => {
    const updatedDate = new Date(value)
    updatedDate.setMinutes(parseInt(minute))
    onChange(updatedDate)
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "w-[240px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={date} onSelect={handleDateChange} initialFocus />
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-gray-500" />

        <Select value={value.getHours().toString()} onValueChange={handleHourChange}>
          <SelectTrigger className="w-[90px]">
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {hourOptions.map((hour) => (
              <SelectItem key={hour} value={hour.toString()}>
                {hour < 12
                  ? hour === 0
                    ? "12 AM"
                    : `${hour} AM`
                  : hour === 12
                    ? "12 PM"
                    : `${hour - 12} PM`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span>:</span>

        <Select value={value.getMinutes().toString()} onValueChange={handleMinuteChange}>
          <SelectTrigger className="w-[70px]">
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            {minuteOptions.map((minute) => (
              <SelectItem key={minute} value={minute.toString()}>
                {minute === 0 ? "00" : minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
